import { Icon, mediaQueries, Text, LoadingState, style } from '@do/walrus';
import React, { ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { CONTACT_SUPPORT_URL, HEADER_HEIGHT } from '../constants';
import copy from '../copy';
import Banner from './Banner';
import Header from './Header';
import Link from './Link';

const Wrap = styled.div`
  max-width: ${style.viewports.xlarge}px;
  position: relative;
  padding: ${HEADER_HEIGHT}px 0 ${style.vars.whitespace.xlarge};
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  margin: auto;

  ${mediaQueries.smallMin} {
    padding-bottom: ${style.vars.whitespace.large};
  }
`;

const WrapSplit = styled(Wrap)`
  padding: 0 0 ${style.vars.whitespace.regular};

  ${mediaQueries.mediumMin} {
    padding: ${HEADER_HEIGHT + 85}px 0 ${style.vars.whitespace.regular};
  }
`;

const LoadingWrap = styled.div`
  position: absolute;
  left: 50%;
  top: ${HEADER_HEIGHT}px;
  transform: translate(-50%, ${style.vars.whitespace.xlarge});
`;

const FormWrap = styled.div`
  position: relative;
  z-index: ${style.vars.zIndex.base};
  background: ${style.colors.white};
  box-sizing: border-box;
  border: ${style.vars.borderWidth.thin} solid rgba(91, 105, 135, 0.22);
  border-radius: ${style.vars.borderRadius.base};
  margin-top: 10vh;
  box-shadow: 0px ${style.vars.space['1']} ${style.vars.space['1']} rgba(0, 0, 0, 0.25);
  overflow: auto; /* handle the case when the form is smaller than min-width of TextInput */
  padding-top: ${style.vars.whitespace.medium};
  padding-bottom: ${style.vars.whitespace.medium};
  height: 100%;

  width: 350px;
  ${mediaQueries.smallMin} {
    width: ${style.viewports.small}px;
  }
`;

export const SectionWithFullScreenBackground = styled.section`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100vw;
    height: calc(100% + ${style.vars.space['4']});
    left: 0;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

    z-index: -1;

    ${mediaQueries.smallMax} {
      min-width: calc(100% + ${style.vars.space['4']} * 2);
      right: -${style.vars.space['4']};
      left: -${style.vars.space['4']};
    }
  }
`;

const ButtonImage = styled.img`
  width: ${style.vars.space['4']};
  height: ${style.vars.space['4']};
  margin-top: -${style.vars.whitespace.xsmall};
  margin-right: ${style.vars.whitespace.small};
  display: inline-block;
`;

const ButtonImageBg = styled.div`
  display: inline-block;
  vertical-align: middle;
  background: ${style.colors.white};
  border-radius: ${style.vars.borderRadius.base};
  height: ${style.vars.space['5']};
  width: ${style.vars.space['5']};
  margin-right: ${style.vars.whitespace.small};
  margin-top: -2px;

  ${ButtonImage} {
    vertical-align: initial;
    position: relative;
    top: 1px;
    left: ${style.vars.whitespace.xsmall};
  }
`;

const NoUnderlineLink = styled(Link)`
  text-decoration: none;
`;

// This function will replace a node in a string with a React element.
// The key will be in format {{key_name}} and the node will be replaced with the React element.
// If the replacer does not contain the key, it will return the original text. The order of the keys in the replacer array does not matter.
// If the text does not contain any keys, the original text will be returned.
export function interpolateNodes(text: string, replacer: { key: string; node: React.ReactNode }[]) {
  try {
    if (!text) {
      return <>{text}</>;
    }

    const regex = /{{(.*?)}}/g;
    const parts = text.split(regex);
    const result: React.ReactNode[] = [];

    parts.forEach((part, index) => {
      if (index % 2 === 0) {
        // Even index: plain text
        result.push(<>{part}</>);
      } else {
        // Odd index: key to be replaced
        const key = part.trim();
        const match = replacer.find((item) => item.key === key);
        if (match) {
          result.push(match.node);
        } else {
          result.push(key);
        }
      }
    });

    return <>{result}</>;
  } catch (error) {
    return text;
  }
}

interface LoadingProps {
  isLogin?: boolean;
}

const Loading = ({ isLogin }: LoadingProps): ReactElement => {
  return (
    <>
      <Header isLogin={isLogin} />
      <Wrap>
        <LoadingWrap className="do-loader">
          <LoadingState sammy={true} />
        </LoadingWrap>
      </Wrap>
    </>
  );
};

export interface ButtonProps {
  url?: string;
  text: string;
  onClick: (e: any) => void;
  disabled?: boolean;
  dataTrackingId?: string;
}

export interface Err {
  error: {
    title?: string;
    details: string;
    requestId?: string;
  } | null;
}
const ErrorBanner = (errObject: Err): ReactElement => {
  const error = errObject?.error || copy.errors.unknown;
  const requestIdContent = useMemo(() => {
    if ('requestId' in error && error.requestId) {
      return (
        <div>
          ({copy.request_id}: {error.requestId})
        </div>
      );
    }
    return null;
  }, [error]);

  const containsInterpolation = error.details.includes('{{');

  if (containsInterpolation) {
    return (
      <Banner type="error" {...error}>
        {interpolateNodes(error.details, [
          {
            key: 'signUp',
            node: <NoUnderlineLink to="/registrations/new">sign up</NoUnderlineLink>,
          },
          {
            key: 'contact',
            node: (
              <NoUnderlineLink to={CONTACT_SUPPORT_URL} forceExternal>
                contact support
              </NoUnderlineLink>
            ),
          },
        ])}
        {requestIdContent}
      </Banner>
    );
  } else {
    return (
      <Banner type="error" {...error}>
        {requestIdContent}
      </Banner>
    );
  }
};

export interface Click {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}
const ClickLink = ({ onClick, children, disabled }: Click): ReactElement => {
  return (
    <NoUnderlineLink
      role="button"
      to="/login"
      tabIndex={0}
      // disabled is not  valid property for an html a tag. We have styles that
      // makes it seem to be disabled, but an a tag cannot be disabled.
      {...({ disabled: disabled } as any)}
      onClick={(e) => {
        onClick();
        e.preventDefault();
      }}
    >
      {children}
    </NoUnderlineLink>
  );
};

enum BannerType {
  Success = 'alert-success',
}

interface HeaderBannerProps {
  type: BannerType;
  text: string;
  linkText?: string;
  url?: string;
}

const BannerWithIcon = styled.div`
  position: fixed;
  top: 65px;
  right: 0;
  left: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 12px;
  padding: ${style.vars.whitespace.small} ${style.vars.whitespace.medium};
  align-items: center;
  z-index: 2;
  flex-wrap: none;
  background: ${style.colors.green.lightest};
`;

const HeaderBanner = ({ type, text, linkText, url = '' }: HeaderBannerProps): ReactElement => (
  <BannerWithIcon>
    <Icon icon={type} large={true} />
    <Text type="p">
      <strong>{text}</strong>
      {linkText && (
        <Link to={url} type="external">
          {linkText}
        </Link>
      )}
    </Text>
  </BannerWithIcon>
);

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;

  ${mediaQueries.mediumMin} {
    flex-wrap: nowrap;
  }
`;

const Column = styled.div`
  flex-basis: 100%;

  ${mediaQueries.mediumMin} {
    flex-basis: 50%;
  }
`;

export {
  Wrap,
  WrapSplit,
  LoadingWrap,
  FormWrap,
  ButtonImage,
  ButtonImageBg,
  ErrorBanner,
  ClickLink,
  NoUnderlineLink,
  Loading,
  HeaderBanner,
  BannerType,
  Row,
  Column,
};
